import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

// Affiliate endpoints live under /api/affiliate and use the customer JWT
// (kt-auth-react-v in localStorage), same as the rest of the user app.
const affiliateApi = axios.create()
affiliateApi.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('kt-auth-react-v')
    const token = raw ? JSON.parse(raw)?.api_token : null
    if (token && config.headers) config.headers.Authorization = `Bearer ${token}`
  } catch (_) {}
  return config
})

export interface AffiliateTier {
  min: number
  rate: number
}

export interface AffiliateDashboard {
  currency: string
  tiers: AffiliateTier[]
  affiliate_code: string
  referred_customers: number
  current_month: string
  current_month_payers: number
  projected_rate: number
  projected_earnings: number
  whatsapp_status: string
  payouts: Array<{_id: string; count: number; total: number; statuses: string[]}>
}

export interface AffiliateCustomer {
  _id: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  company_name?: string
  trial_period_end?: string
  is_paying: boolean
  trial_active: boolean
  commission_status: string | null
}

export function getAffiliateDashboard() {
  return affiliateApi.get<AffiliateDashboard>(`${API_URL}/api/affiliate/dashboard`)
}

export function getAffiliateCustomers() {
  return affiliateApi.get<AffiliateCustomer[]>(`${API_URL}/api/affiliate/customers`)
}

export function addAffiliateCustomer(data: {
  name: string
  email: string
  phone: string
  company_name?: string
  password?: string
}) {
  return affiliateApi.post(`${API_URL}/api/affiliate/customer/add`, data)
}

export interface CustomerProfile {
  _id: string
  name: string
  facebook_id: string | null
  connected: boolean
  active: boolean
  last_health_check: string | null
  // How this profile gets connected, decided server-side from the admin
  // "profile QR login" flag: 'password' → login_url is the /c/:code page where
  // the client types their FB email + password; 'qr' → the FB login URL to scan.
  login_method?: 'password' | 'qr'
  login_url?: string
  // The username/password connect link (/c/:code), always provided so the rep can
  // share it as an alternative to the QR even when the account is in QR mode.
  password_url?: string
  // When the client actually typed their FB email + password into login_url.
  // Null until they do. Not the same as `connected` — a rep finishes the login
  // afterwards, so this flips first and is the affiliate's progress signal.
  credentials_submitted_at?: string | null
  // Link to this profile's LIVE browser, so the affiliate can go in and confirm
  // the client actually landed connected (and finish 2FA if it's sitting there).
  // Minted with the list — opening it does not start anything on its own.
  preview_url?: string
}

export function getCustomerProfiles(customerId: string) {
  return affiliateApi.get<CustomerProfile[]>(`${API_URL}/api/affiliate/customer/${customerId}/profiles`)
}
export function startCustomerProfileLogin(customerId: string, profileId: string) {
  return affiliateApi.post(`${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/login-start`)
}
export function getCustomerProfileQr(customerId: string, profileId: string) {
  // `code` = FB pairing code (3-3-4) shown next to the QR; the customer can type
  // it instead of scanning.
  return affiliateApi.get<{status: string; url?: string; method?: string; code?: string | null}>(`${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/login-qr`)
}
// The post-scan device-verification confirmation: the 2-digit number FB asks the
// customer to tap on their phone to finish logging in. `status: 'none'` until the
// bot captures one (appears a few seconds after the scan).
export function getCustomerProfileAuthApproval(customerId: string, profileId: string) {
  return affiliateApi.get<{status: string; code?: string; url?: string | null; age_sec?: number}>(`${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/auth-approval`)
}
// "Barcode stuck" reset: deletes the unconnected placeholder and creates a fresh
// profile (new profileId) with its bot spawned, so a new login QR starts capturing.
export function resetCustomerProfileBarcode(customerId: string, profileId: string) {
  return affiliateApi.post<{success: boolean; profileId?: string; error?: string}>(
    `${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/reset-barcode`
  )
}
// The affiliate's own "🔗 קישור ללקוח": a password-free link to this one
// profile's LIVE browser, so the affiliate can do the Facebook login for the
// client instead of walking them through it. Scoped to the single profile by an
// HMAC the fleet re-checks — it reaches nothing else.
export function getCustomerProfilePreviewLink(customerId: string, profileId: string, days = 7) {
  return affiliateApi.get<{success: boolean; url: string; days: number; expires_at: string}>(
    `${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/preview-link?days=${days}`
  )
}
export function saveProfilePhone(customerId: string, profileId: string, phone: string) {
  return affiliateApi.put(`${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/phone`, {phone})
}
export function sendProfileLoginLink(customerId: string, profileId: string, phone: string) {
  return affiliateApi.post(`${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/send-login-link`, {phone})
}
export function sendProfileLoginSms(customerId: string, profileId: string, phone: string) {
  return affiliateApi.post(`${API_URL}/api/affiliate/customer/${customerId}/profile/${profileId}/send-sms`, {phone})
}
export function bulkSendLoginLinks(customerId: string, items: Array<{profileId: string; phone: string}>) {
  return affiliateApi.post<{success: boolean; sent: number; failed: any[]}>(`${API_URL}/api/affiliate/customer/${customerId}/profiles/send-login-links`, {items})
}

export function loginAsCustomer(customerId: string) {
  return affiliateApi.post<{success: boolean; api_token: string}>(
    `${API_URL}/api/affiliate/customer/${customerId}/login-as`
  )
}

// ---- WhatsApp ----
export function getWhatsappStatus() {
  return affiliateApi.get(`${API_URL}/api/affiliate/whatsapp/status`)
}
export function connectWhatsapp() {
  return affiliateApi.post(`${API_URL}/api/affiliate/whatsapp/connect`)
}
export function logoutWhatsapp() {
  return affiliateApi.post(`${API_URL}/api/affiliate/whatsapp/logout`)
}
export interface WaMedia { url: string; type: string }
export function sendWhatsapp(customerId: string, message: string, media?: WaMedia[]) {
  return affiliateApi.post(`${API_URL}/api/affiliate/whatsapp/send`, {customerId, message, media})
}
export function sendWhatsappToLead(leadId: string, message: string, media?: WaMedia[]) {
  return affiliateApi.post(`${API_URL}/api/affiliate/whatsapp/send`, {leadId, message, media})
}
// Upload media files; returns CDN urls to attach to a template / message.
export function uploadWaTemplateMedia(files: File[]) {
  const fd = new FormData()
  files.forEach((f) => fd.append('files', f))
  return affiliateApi.post<{media: WaMedia[]}>(`${API_URL}/api/affiliate/whatsapp/template-media`, fd)
}
// Edit a referred customer's account details (name/phone/email/company).
export function editCustomer(customerId: string, data: {first_name?: string; last_name?: string; phone?: string; email?: string; company_name?: string}) {
  return affiliateApi.put<{success: boolean; customer?: any; error?: string}>(`${API_URL}/api/affiliate/customer/${customerId}`, data)
}
// Send the onboarding welcome message to a customer, over WhatsApp or SMS.
export function sendWelcomeMessage(customerId: string, channel: 'whatsapp' | 'sms' = 'whatsapp') {
  return affiliateApi.post<{success: boolean; welcome_sent?: boolean; welcome_sent_at?: string; channel?: string; error?: string}>(`${API_URL}/api/affiliate/customer/${customerId}/send-welcome`, {channel})
}
// Get the welcome message text + phone WITHOUT sending — used for the manual
// "send via WhatsApp Web" (wa.me) fallback so the affiliate never gets stuck.
export function getWelcomePreview(customerId: string) {
  return affiliateApi.post<{success: boolean; message?: string; phone?: string; error?: string}>(`${API_URL}/api/affiliate/customer/${customerId}/send-welcome`, {preview: true})
}
// Build a WhatsApp Web / app share link (wa.me) to a number with a prefilled text.
// Normalizes IL phones (0X… → 972X…); with no number, opens the contact picker.
export function waWebUrl(phone: string, text: string) {
  const d = String(phone || '').replace(/\D/g, '')
  const intl = d.startsWith('972') ? d : d.startsWith('0') ? '972' + d.slice(1) : d
  return intl ? `https://wa.me/${intl}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`
}
export interface WaTemplate { title: string; text: string; media?: WaMedia[] }
export function getWaTemplates() {
  return affiliateApi.get<{templates: WaTemplate[]; admin_templates?: WaTemplate[]; status: string}>(`${API_URL}/api/affiliate/whatsapp/templates`)
}
export function saveWaTemplates(templates: WaTemplate[]) {
  return affiliateApi.put<{success: boolean; templates: WaTemplate[]}>(`${API_URL}/api/affiliate/whatsapp/templates`, {templates})
}
// Admin-only: set the templates pushed to ALL affiliates. Requires an admin token
// (sent as X-Admin-Token) on top of the affiliate session.
export function saveAdminWaTemplates(templates: WaTemplate[]) {
  const adminToken = localStorage.getItem('admin_auth_token') || ''
  return affiliateApi.put<{success: boolean; admin_templates: WaTemplate[]}>(
    `${API_URL}/api/affiliate/whatsapp/admin-templates`,
    {templates},
    {headers: {'X-Admin-Token': adminToken}}
  )
}
// Send the affiliate's self-registration link to a prospect's phone via the
// affiliate's connected WhatsApp. `base` should be the current app origin so the
// link points back to this same deployment.
export function sendRegisterLink(phone: string, base: string, message?: string) {
  return affiliateApi.post<{sent?: boolean; link?: string; error?: string}>(`${API_URL}/api/affiliate/whatsapp/send-register-link`, {phone, base, message})
}
export function getWaHistory(params: {customerId?: string; leadId?: string}) {
  return affiliateApi.get<{messages: Array<{_id: string; message: string; phone: string; created_at: string}>}>(`${API_URL}/api/affiliate/whatsapp/history`, {params})
}

// ---- Agents pool + leads ----
export function getAgents(params: Record<string, any>) {
  return affiliateApi.get(`${API_URL}/api/affiliate/agents`, {params})
}
export function getAgentStats() {
  return affiliateApi.get(`${API_URL}/api/affiliate/agents/stats`)
}
export function autocompleteAgents(q: string) {
  return affiliateApi.get(`${API_URL}/api/affiliate/agents/autocomplete`, {params: {q}})
}
export function grabLeads(count = 10) {
  return affiliateApi.post(`${API_URL}/api/affiliate/leads/grab`, {count})
}
// Claim ONE specific agent (found via the pool search) into the affiliate's leads.
export function claimAgentLead(agent: Record<string, any>) {
  return affiliateApi.post<{success: boolean; lead?: any; error?: string; lead_state?: string}>(`${API_URL}/api/affiliate/leads/claim`, {agent})
}
export function getLeads() {
  return affiliateApi.get(`${API_URL}/api/affiliate/leads`)
}
export function editLead(id: string, data: Record<string, any>) {
  return affiliateApi.post(`${API_URL}/api/affiliate/lead/edit/${id}`, data)
}
export function addLeadComment(id: string, comment: string) {
  return affiliateApi.post(`${API_URL}/api/affiliate/lead/add-comment/${id}`, {comment})
}
export function convertLead(id: string, data: Record<string, any> = {}) {
  return affiliateApi.post(`${API_URL}/api/affiliate/lead/${id}/convert`, data)
}
// Gemini + Google Search lookup of the agent's real (non-virtual) phone numbers.
// May return multiple numbers (office line, mobile, agency switchboard).
export function findLeadRealPhone(id: string) {
  return affiliateApi.post<{success: boolean; real_phones: Array<{phone: string; display_phone?: string; source?: string; found_at?: string}>; found_now: number; error?: string}>(
    `${API_URL}/api/affiliate/lead/${id}/find-real-phone`,
    {}
  )
}
export function getAgentListings(phone: string) {
  return affiliateApi.get(`${API_URL}/api/affiliate/agents/listings`, {params: {phone}})
}
export function prepareCampaigns(customerId: string, cities: string[], commercial?: boolean) {
  return affiliateApi.post<{campaigns: Record<string, string>}>(`${API_URL}/api/affiliate/agents/prepare-campaigns`, {customerId, cities, commercial})
}
// Re-sync an agent's yad2 listings into a customer: update changed posts + add new ones.
export function syncAgentPosts(payload: {customerId: string; phone: string; contactPhone?: string; licenseText?: string; agentName?: string; agencyName?: string; includePrice?: boolean; useAI?: boolean; autoDaily?: boolean; includeNoImages?: boolean}) {
  return affiliateApi.post<{success: boolean; updated?: number; added?: number; auto_daily?: boolean; error?: string}>(`${API_URL}/api/affiliate/agents/sync`, payload)
}
// Enable/save daily auto-sync settings without running a sync now.
export function setAgentSyncSettings(payload: {customerId: string; phone?: string; enabled?: boolean; contactPhone?: string; licenseText?: string; agentName?: string; agencyName?: string; includePrice?: boolean; useAI?: boolean; includeNoImages?: boolean}) {
  return affiliateApi.post<{success: boolean; enabled?: boolean; error?: string}>(`${API_URL}/api/affiliate/agents/sync-settings`, payload)
}
export function importAgentAI(payload: {customerId: string; phone?: string; tokens?: string[]; campaignName?: string; useAI?: boolean; campaignId?: string; contactPhone?: string; licenseText?: string; agentName?: string; agencyName?: string; includePrice?: boolean; includeNoImages?: boolean}) {
  return affiliateApi.post(`${API_URL}/api/affiliate/agents/import-ai`, payload)
}

// ---- yad2 ----
export function getYad2Cities() {
  return affiliateApi.get<Array<{city: string; count: number}>>(`${API_URL}/api/affiliate/yad2/cities`)
}
export function searchYad2(params: Record<string, any>) {
  return affiliateApi.get(`${API_URL}/api/affiliate/yad2/search`, {params})
}
export function importYad2(customerId: string, tokens: string[], campaignName?: string, licenseText?: string) {
  return affiliateApi.post(`${API_URL}/api/affiliate/yad2/import`, {customerId, tokens, campaignName, licenseText})
}

// ---- meetings (booked by the affiliate into the back office's calendar) ----
export interface MeetingSlot {
  time: string
  minutes: number
  taken: boolean
  past: boolean
}
export interface AffiliateMeeting {
  _id: string
  meeting_date: string
  meeting_type: string
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show'
  title: string
  notes?: string
  created_at?: string
  booked_by_me: boolean
  customer: {_id: string | null; lead_id?: string; is_lead?: boolean; name: string; phone?: string} | null
  admin: {_id: string; name: string} | null
}
export interface MeetingCustomer {
  _id: string | null
  // Leads have no account yet — they're booked by lead_id instead of _id.
  lead_id?: string | null
  is_lead?: boolean
  name: string
  phone?: string
  email?: string
  company_name?: string
  upcoming_meeting: string | null
}

export function getMeetingAdmins() {
  return affiliateApi.get<{admins: Array<{_id: string; name: string}>}>(`${API_URL}/api/affiliate/meetings/admins`)
}
export function getMeetingCustomers() {
  return affiliateApi.get<MeetingCustomer[]>(`${API_URL}/api/affiliate/meetings/customers`)
}
export function getMeetingSlots(adminId: string, date: string) {
  return affiliateApi.get<{date: string; slots: MeetingSlot[]}>(`${API_URL}/api/affiliate/meetings/slots`, {
    params: {admin_id: adminId, date},
  })
}
export function getAffiliateMeetings(params: {customer?: string; status?: string; upcoming?: string} = {}) {
  return affiliateApi.get<{meetings: AffiliateMeeting[]}>(`${API_URL}/api/affiliate/meetings`, {params})
}
export function bookAffiliateMeeting(payload: {
  // Exactly one of customer (a referred account) / lead (a lead with no account yet)
  customer?: string
  lead?: string
  assigned_to: string
  meeting_date: string
  meeting_type?: string
  notes?: string
}) {
  return affiliateApi.post<{success: boolean; meeting: AffiliateMeeting}>(`${API_URL}/api/affiliate/meetings`, payload)
}
export function cancelAffiliateMeeting(id: string) {
  return affiliateApi.post<{success: boolean}>(`${API_URL}/api/affiliate/meetings/${id}/cancel`, {})
}

// ---- Sales PBX: click-to-call + call AI summaries ----
export interface PbxExtension {ext: string; online: boolean; busy: boolean; agentName: string | null}
export interface PbxCall {
  id: string; at: number; endedAt?: number; phone: string; name?: string; source: string
  agentName?: string; ext?: string; by?: string; result?: string; note?: string
  recSecs?: number; hasRecording: boolean; aiState?: string
  ai?: {summary: string; key_points: string[]; next_step: string; follow_up_time: string; outcome: string; customer_name?: string} | null
  transcript?: {who: string; at: number; text: string}[]
}
export function getPbxConfig() {
  return affiliateApi.get<{available: boolean; extension: string; extensions: PbxExtension[]; error?: string}>(`${API_URL}/api/affiliate/pbx/config`)
}
export function setPbxExtension(extension: string) {
  return affiliateApi.put<{success: boolean; extension: string}>(`${API_URL}/api/affiliate/pbx/extension`, {extension})
}
export function pbxDial(rowId: string, phone: string, ext?: string) {
  return affiliateApi.post<{success: boolean; ext: string; phone: string; error?: string}>(`${API_URL}/api/affiliate/pbx/dial`, {rowId, phone, ext})
}
export function getPbxStatus(ext?: string) {
  return affiliateApi.get<{state: string | null; phone?: string}>(`${API_URL}/api/affiliate/pbx/status`, {params: ext ? {ext} : {}})
}
export function getPbxRowCalls(rowIds: string[]) {
  return affiliateApi.post<Record<string, {count: number; last: PbxCall}>>(`${API_URL}/api/affiliate/pbx/row-calls`, {rowIds})
}
export function getPbxCallsForRow(rowId: string) {
  return affiliateApi.get<{name: string; phones: string[]; calls: PbxCall[]}>(`${API_URL}/api/affiliate/pbx/row/${rowId}/calls`)
}
export function getPbxCall(callId: string, rowId: string) {
  return affiliateApi.get<PbxCall>(`${API_URL}/api/affiliate/pbx/call/${callId}`, {params: {row: rowId}})
}
// <audio src> can't send the Bearer header, so the token goes in the query string.
export function pbxRecordingUrl(callId: string, rowId: string) {
  let token = ''
  try { token = JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}')?.api_token || '' } catch (_) {}
  return `${API_URL}/api/affiliate/pbx/call/${callId}/recording?row=${encodeURIComponent(rowId)}&access_token=${encodeURIComponent(token)}`
}
// Incoming call on my extension right now (for the side popup).
export function getPbxLive() {
  return affiliateApi.get<{
    state: 'ringing' | 'connected' | null; since?: number; phone?: string; ext?: string
    lead?: {_id: string; name: string; agency_name?: string; status?: string; follow_up_time?: string; last_note?: string} | null
    customer?: {_id: string; name: string; company_name?: string; trial_period_end?: string} | null
    lastCall?: PbxCall | null
  }>(`${API_URL}/api/affiliate/pbx/live`)
}
// Missed incoming calls for me, each with the lead/customer row to call back.
export function getPbxMissed() {
  return affiliateApi.get<{id: string; at: number; phone: string; name: string; row: {_id: string; name: string; agency_name?: string} | null}[]>(`${API_URL}/api/affiliate/pbx/missed`)
}
export function markPbxMissedHandled(id: string) {
  return affiliateApi.post<{success: boolean}>(`${API_URL}/api/affiliate/pbx/missed/${id}/handled`, {})
}

// Inside the Android app: the app phone's own extension (created on first call).
export const inAffiliateApp = () => typeof navigator !== 'undefined' && /FBZipperAffiliateApp/.test(navigator.userAgent)
export function getPbxSoftphone() {
  return affiliateApi.get<{success: boolean; ext: string; name: string}>(`${API_URL}/api/affiliate/pbx/softphone`)
}
